// Section for import npm components -----------------------------------------------------------------------------------
import React, { useContext } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { format } from 'date-fns';

// Material UI ( mui )  components
import Box from '@mui/material/Box';

// Section for import custom components --------------------------------------------------------------------------------
import { MansionMarketPriceHistoryContext } from '@providers/MansionDetail/MansionMarketPriceHistory/MansionMarketPriceHistoryProvider';

// Section for type definition -----------------------------------------------------------------------------------------
import { MarketPriceHistoryItem } from '@/types/components/MansionDetail/MarketPriceHistoryPropsData';
import '@styles/mansions/detail.scss';

type ChartProps = {
  legendState: {
    series1: boolean;
    series2: boolean;
    series3: boolean;
    series4: boolean;
  };
  toggleLegendState: (seriesKey: keyof ChartProps['legendState']) => void;
  averageSquareMeter: number;
};

export default function Chart({ legendState, toggleLegendState, averageSquareMeter }: ChartProps) {
  const { marketPriceHistoryData } = useContext(MansionMarketPriceHistoryContext);
  let marketPriceHistories;
  if (
    typeof marketPriceHistoryData === 'object' &&
    marketPriceHistoryData.marketPriceHistoryMansions &&
    typeof marketPriceHistoryData.marketPriceHistoryMansions === 'object'
  ) {
    marketPriceHistories = marketPriceHistoryData.marketPriceHistoryMansions.marketPriceHistories;
  } else if (
    typeof marketPriceHistoryData === 'object' &&
    marketPriceHistoryData.marketPriceHistoryCities &&
    typeof marketPriceHistoryData.marketPriceHistoryCities === 'object'
  ) {
    marketPriceHistories = marketPriceHistoryData.marketPriceHistoryCities.marketPriceHistories;
  } else if (
    typeof marketPriceHistoryData === 'object' &&
    marketPriceHistoryData.marketPriceHistoryTowns &&
    typeof marketPriceHistoryData.marketPriceHistoryTowns === 'object'
  ) {
    marketPriceHistories = marketPriceHistoryData.marketPriceHistoryTowns.marketPriceHistories;
  } else if (
    typeof marketPriceHistoryData === 'object' &&
    marketPriceHistoryData.marketPriceHistoryStations &&
    typeof marketPriceHistoryData.marketPriceHistoryStations === 'object'
  ) {
    marketPriceHistories = marketPriceHistoryData.marketPriceHistoryStations.marketPriceHistories;
  }

  let dates: string[] = [];
  if (marketPriceHistories && typeof marketPriceHistories === 'object') {
    dates = Object.keys(marketPriceHistories);
  }

  const getTsuboData = (dataObject: { [date: string]: MarketPriceHistoryItem }) => {
    // console.log(dataObject);
    return dates.map((date) => {
      if (!dataObject[date]) {
        return 0;
      }
      const price = dataObject[date].pricePerTsubo ? dataObject[date].pricePerTsubo : 0;
      return price / 10000;
    });
  };

  const series = [];

  if (
    typeof marketPriceHistoryData === 'object' &&
    marketPriceHistoryData.marketPriceHistoryMansions &&
    typeof marketPriceHistoryData.marketPriceHistoryMansions === 'object'
  ) {
    if (typeof marketPriceHistoryData.marketPriceHistoryMansions.marketPriceHistories === 'object') {
      series.push({
        name: `赤線 = ${marketPriceHistoryData.marketPriceHistoryMansions.mansionName}の売買相場`,
        className: 'mansions',
        visible: legendState.series1,
        data: getTsuboData(marketPriceHistoryData.marketPriceHistoryMansions.marketPriceHistories),
        lineWidth: 4,
        color: '#FF3C00',
        zIndex: 4,
        events: {
          legendItemClick() {
            toggleLegendState('series1');
            return false;
          },
        },
      });
    }
  }

  if (
    typeof marketPriceHistoryData === 'object' &&
    marketPriceHistoryData.marketPriceHistoryCities &&
    typeof marketPriceHistoryData.marketPriceHistoryCities === 'object'
  ) {
    if (typeof marketPriceHistoryData.marketPriceHistoryCities.marketPriceHistories === 'object') {
      series.push({
        name: `青線 = ${marketPriceHistoryData.marketPriceHistoryCities.cityName}の売買相場`,
        className: 'cities',
        visible: legendState.series2,
        data: getTsuboData(marketPriceHistoryData.marketPriceHistoryCities.marketPriceHistories),
        lineWidth: 4,
        color: '#00B4FF',
        dashStyle: 'ShortDot',
        zIndex: 3,
        events: {
          legendItemClick() {
            toggleLegendState('series2');
            return false;
          },
        },
      });
    }
  }

  if (
    typeof marketPriceHistoryData === 'object' &&
    marketPriceHistoryData.marketPriceHistoryTowns &&
    typeof marketPriceHistoryData.marketPriceHistoryTowns === 'object'
  ) {
    if (typeof marketPriceHistoryData.marketPriceHistoryTowns.marketPriceHistories === 'object') {
      series.push({
        name: `紫線 = ${marketPriceHistoryData.marketPriceHistoryTowns.townName}の売買相場`,
        className: 'towns',
        visible: legendState.series3,
        data: getTsuboData(marketPriceHistoryData.marketPriceHistoryTowns.marketPriceHistories),
        lineWidth: 4,
        color: '#C800FF',
        dashStyle: 'ShortDot',
        zIndex: 2,
        events: {
          legendItemClick() {
            toggleLegendState('series3');
            return false;
          },
        },
      });
    }
  }
  if (
    typeof marketPriceHistoryData === 'object' &&
    marketPriceHistoryData.marketPriceHistoryStations &&
    typeof marketPriceHistoryData.marketPriceHistoryStations === 'object'
  ) {
    if (typeof marketPriceHistoryData.marketPriceHistoryStations.marketPriceHistories === 'object') {
      series.push({
        name: `緑線 = ${marketPriceHistoryData.marketPriceHistoryStations.stationName}の売買相場`,
        className: 'stations',
        visible: legendState.series4,
        data: getTsuboData(marketPriceHistoryData.marketPriceHistoryStations.marketPriceHistories),
        lineWidth: 4,
        color: '#37C80A',
        dashStyle: 'ShortDot',
        zIndex: 1,
        events: {
          legendItemClick() {
            toggleLegendState('series4');
            return false;
          },
        },
      });
    }
  }

  const options = {
    chart: {
      height: 450,
      type: 'line',
      spacing: [0, 0, 0, 0], // チャート要素の余白を調整
      style: {
        fontFamily:
          "'MyYuGothicM','游ゴシック','Yu Gothic','YuGothic','ヒラギノ角ゴ ProN','Hiragino Kaku Gothic ProN','MS メイリオ','Meiryo','sans-serif'", // ここでグローバルなフォントファミリーを設定
      },
    },
    tooltip: {
      enabled: true, // tooltipを有効にする
      shared: false, // 共有tooltipを有効にする（複数のシリーズがある場合に便利）
      useHTML: true, // HTMLタグをtooltip内で使用可能にする
      style: {
        color: '#191919', // テキストの色
        backgroundColor: '#FFF9AC', // 背景色
        fontSize: '10px',
        fontFamily:
          "'MyYuGothicM','游ゴシック','Yu Gothic','YuGothic','ヒラギノ角ゴ ProN','Hiragino Kaku Gothic ProN','MS メイリオ','Meiryo','sans-serif'",
      },
      formatter() {
        // この関数内で `this` はtooltipを表示しているポイントを指します。
        const { point }: any = this; // 現在のポイントにアクセス
        const xAxisLabel: string = point.category; // x軸のラベル（年月）を取得
        const squareMeter: number = (point.y / 3.30579) * averageSquareMeter; // y軸の値を坪数に変換
        // tooltipのHTMLをカスタマイズ
        return `<b>${xAxisLabel}<br/>${squareMeter.toFixed(0)}万円<br>(${point.y.toFixed(0)}万円/坪)</b>`;
      },
    },
    title: {
      text: '',
    },
    xAxis: {
      categories: dates.map((date) => format(new Date(date), 'yyyy年MM月')),
      labels: {
        rotation: 300, // ラベルを水平に表示
        style: {
          fontSize: '10px',
          fontFamily:
            "'MyYuGothicM','游ゴシック','Yu Gothic','YuGothic','ヒラギノ角ゴ ProN','Hiragino Kaku Gothic ProN','MS メイリオ','Meiryo','sans-serif'",
        },
      },
    },
    yAxis: [
      {
        title: {
          text: '',
        },
        labels: {
          formatter(): string {
            // 価格を坪数に変換（1坪 = 3.30579平方メートル）
            const context: any = this;
            const squareMeter = (context.value / 3.30579) * averageSquareMeter;
            // ラベルのフォーマットを定義
            return `${squareMeter.toFixed(0)}万円<br><span style="font-size: 9px;">(${context.value.toFixed(0)}万円/坪)</span>`;
          },
          style: {
            fontSize: '10px',
            fontFamily:
              "'MyYuGothicM','游ゴシック','Yu Gothic','YuGothic','ヒラギノ角ゴ ProN','Hiragino Kaku Gothic ProN','MS メイリオ','Meiryo','sans-serif'",
          },
        },
        opposite: false,
      },
    ],
    legend: {
      itemStyle: {
        fontSize: '14px',
        fontFamily:
          "'MyYuGothicM','游ゴシック','Yu Gothic','YuGothic','ヒラギノ角ゴ ProN','Hiragino Kaku Gothic ProN','MS メイリオ','Meiryo','sans-serif'",
      },
      layout: 'horizontal', // 水平に項目を並べる
      align: 'center', // 中央揃え
      verticalAlign: 'top', // 上部に配置
      borderWidth: 0, // 凡例のボーダー（オプション）
      margin: 0, // 凡例のマージンを設定
      padding: 24, // 凡例内のパディングを設定
      y: -10, // 凡例を上に10px移動させる
      itemMarginTop: 10, // 判例項目間の上の余白を調整
      itemMarginBottom: 10, // 判例項目間の下の余白を調整
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      line: {
        dataLabels: {
          enabled: false,
        },
        enableMouseTracking: true,
      },
    },
    series,
  };

  return (
    <Box className="w-full px-5">
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Box>
  );
}
